import { EMessengerAppName, TMessengerAppParams } from '@xometry/ui';
import cx from 'clsx';
import { FC, useCallback, useEffect, useMemo, useRef } from 'react';

import styles from './MicroFrontend.module.less';

// It can be overloaded by adding union
interface IProps {
  name: 'Messenger';
  host: string;
  appParams: TMessengerAppParams<EMessengerAppName.PARTNERS_MAIN>;
}

declare const window: any;

interface TManifest {
  files: Record<string, string>;
}

const ts = new Date().getTime();
const tsParam = `ts=${ts}`;

export const MicroFrontend: FC<IProps> = ({ name, host, appParams }) => {
  const hasLoadedRef = useRef(false);

  const containerId = `${name}-container`;

  const renderMicroFrontend = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    window[`render${name}`]?.(containerId, appParams);
  }, [appParams, containerId, name]);

  const unmountMicroFrontend = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    window[`unmount${name}`]?.(containerId);
  }, [containerId, name]);

  const loadMicroFrontend = useMemo(
    () => async (scriptId: string) => {
      const response = await fetch(`${host}/asset-manifest.json?${tsParam}`);
      const manifest = (await response.json()) as TManifest;

      const script = document.createElement('script');
      script.id = scriptId;
      script.crossOrigin = '';
      script.type = 'module';

      const scriptSrc = manifest.files['main.js'];
      script.src = scriptSrc.startsWith('http') ? scriptSrc : `${host}${scriptSrc}`;

      script.onload = renderMicroFrontend;

      const CSSFileName = 'main.css';

      if (manifest.files[CSSFileName]) {
        const CSSLink = document.createElement('link');
        CSSLink.type = 'text/css';
        CSSLink.rel = 'stylesheet';

        const CSSLinkHref = manifest.files[CSSFileName];
        CSSLink.href = CSSLinkHref.startsWith('http') ? CSSLinkHref : `${host}${CSSLinkHref}`;

        CSSLink.onload = () => {
          document.head.appendChild(script);
        };

        document.head.appendChild(CSSLink);
      } else {
        document.head.appendChild(script);
      }
    },
    [host, renderMicroFrontend],
  );

  useEffect(() => {
    return () => {
      unmountMicroFrontend();
    };
  }, [unmountMicroFrontend]);

  useEffect(() => {
    const scriptId = `micro-frontend-script-${name}`;

    if (!hasLoadedRef.current) {
      loadMicroFrontend(scriptId)
        .then(() => {
          hasLoadedRef.current = true;
        })
        .catch(() => {
          throw new Error(`Downloading of ${name} application has failed.`);
        });
    }
  }, [loadMicroFrontend, name]);

  return (
    <div
      id={`${name}-container`}
      className={cx({
        [styles.microFrontendContainer]: true,
      })}
    />
  );
};
